import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatWeight,
  type Exercise,
  type UnitsPreference,
} from "@heraion/shared";

import { slugAsWords, useExerciseNameBySlug } from "../Logger/exerciseName";
import { weightUnitLabels } from "../weightUnitLabels";
import type { InboxParamFormatter } from "./inboxParams";

const RETRY_MS = 30_000;
const MAX_ATTEMPTS = 5;

export type UseInboxParamFormatterOptions = {
  /** The athlete's display unit. */
  unit: UnitsPreference;
  /** The host's catalog read, cache first. */
  loadCatalog: () => Promise<readonly Exercise[] | null>;
};

/**
 * The formatter both hosts hand InboxProvider (#182): `exercise` is a slug,
 * resolved to its localized name; a key ending in `Kg` is canonical kilos,
 * printed in the athlete's unit; `weeks` is a count, said in words. Until the
 * catalog lands, or for an exercise it does not hold, the slug reads as words.
 */
export function useInboxParamFormatter({
  unit,
  loadCatalog,
}: UseInboxParamFormatterOptions): InboxParamFormatter {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language ?? "en-US";
  const [catalog, setCatalog] = useState<readonly Exercise[] | null>(null);
  const nameOf = useExerciseNameBySlug(catalog);
  const loadRef = useRef(loadCatalog);
  useEffect(() => {
    loadRef.current = loadCatalog;
  });

  // Read once at mount; a failed read tries again later, a few times.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = (n: number) => {
      void loadRef
        .current()
        .then((items) => {
          if (cancelled) return;
          if (items) setCatalog(items);
          else throw new Error("no_catalog");
        })
        .catch(() => {
          if (!cancelled && n + 1 < MAX_ATTEMPTS)
            timer = setTimeout(() => attempt(n + 1), RETRY_MS);
        });
    };
    attempt(0);
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, []);

  return useCallback(
    (params) => {
      const labels = weightUnitLabels(t);
      const out: Record<string, string> = { ...params };
      for (const [key, value] of Object.entries(params)) {
        if (key === "exercise") {
          out[key] = nameOf(value, slugAsWords(value));
        } else if (key === "weeks") {
          const n = Number(value);
          if (Number.isInteger(n) && n >= 0)
            out[key] =
              n === 0
                ? t("inbox.maxBump.thisWeek")
                : t("inbox.maxBump.weeksAgo", { count: n });
        } else if (key.endsWith("Kg") && value.trim() !== "") {
          const kg = Number(value);
          if (Number.isFinite(kg)) out[key] = formatWeight(kg, unit, locale, labels);
        }
      }
      return out;
    },
    [t, nameOf, unit, locale],
  );
}
